import { useState } from 'react';
import { publicHref } from '../lib/publicUrls';
import { Link } from 'react-router-dom';
import ProgramJourney from '../components/ProgramJourney';
import ClassSchedule from '../components/ClassSchedule';
import SEO from '../components/SEO';
import { BRAND_ASSETS, MEDIA_SLOTS } from '../lib/mediaTruth';
import { ServiceHero } from '../components/ServiceHeader';
import MX4CoachProfiles, { MX4CoachShowcase } from '../components/MX4CoachProfiles';
import './MX4Page.css';

const equipmentSummary = 'Bosu, Dynamax and Slam Balls, TRX, Landmines, Plyo Boxes, Jump Ropes, Heavy Bags, Resistance Bands, Dumbbells, Kettlebells, Sleds, Rowers, Assault Bikes, Ropes, Plates, and more!';
const equipment = equipmentSummary.replace('!', '').split(', ');

const benefits = [
  'Build functional strength by performing weight-bearing exercises that simulate day-to-day activities.',
  'Burn fat and build muscle faster with high-intensity interval training that activates natural human growth hormone.',
  'Improve power, agility, and cardiovascular health with movements that are easy on your joints.',
  'Increase aerobic capacity, strength, and endurance with unique, efficient workouts.',
];

const MX4Page = () => {
  const [coachesOpen, setCoachesOpen] = useState(false);
  const openCoaches = () => setCoachesOpen(true);
  return (
  <main className="dv-mx4" data-testid="mx4-page">
    <SEO
      title="MX4 Small Group Training | Brentwood"
      description="MX4 at Delta Valley Health Club in Brentwood combines cardio, strength, endurance, and functional training in 45-minute small-group sessions."
      keywords="MX4 Brentwood, small group training Brentwood, HIIT Brentwood"
    />

    <ServiceHero
      id="mx4-title"
      media={MEDIA_SLOTS.programsMx4}
      disclosure="P02 program image: replacement required · M05 logo: provisional Preview use only · both held from production"
      mark={<img className="dv-service-hero__mark" src={BRAND_ASSETS.mx4Logo.src} alt={BRAND_ASSETS.mx4Logo.alt || 'MX4 logo'} data-brand-asset={BRAND_ASSETS.mx4Logo.id} data-brand-status={BRAND_ASSETS.mx4Logo.status} />}
      current="MX4"
      label="Burn Fat. Build Muscle."
      title={<><span>Burn Fat.</span><em>Build Muscle.</em></>}
      lede="Ignite your fitness journey with MX4 classes! Our dynamic 45-minute sessions deliver the ultimate workout."
      actions={<>
        <a className="dv-button dv-button--light" href="#mx4-inquiry">MX4 Membership Inquiry</a>
        <button type="button" className="dv-button dv-button--outline-light" onClick={openCoaches}>Meet the Coaches</button>
        <a className="dv-button dv-button--outline-light" href="#mx4-schedule">View the Schedule</a>
      </>}
      facts={["45-minute sessions", "Small-group coaching", "Cardio, strength & endurance"]}
    />

    <section className="dv-mx4__overview" aria-labelledby="mx4-overview-title">
      <div className="dv-mx4__section-heading">
        <p className="dv-eyebrow">Progressive small-group training</p>
        <h2 className="dv-size-heading" id="mx4-overview-title">
          <span>Meet your level.</span>
          <em>Move it forward.</em>
        </h2>
      </div>
      <div className="dv-mx4__overview-copy">
        <p className="dv-size-body">
          Burn fat, build muscle, enhance agility, and boost cardiovascular
          health in a motivating small group setting. This high-intensity class
          focuses on progression at your level, making it ideal for all fitness
          levels. Start your transformation with us TODAY!
        </p>
      </div>
    </section>

    <section className="dv-mx4__experience" aria-labelledby="mx4-experience-title">
      <div className="dv-mx4__experience-introduction">
        <p className="dv-eyebrow">Inside the workout</p>
        <h2 className="dv-size-heading" id="mx4-experience-title" aria-label="Cardio. Strength. Endurance.">
          <span>Cardio. Strength.</span>
          <em>Endurance.</em>
        </h2>
        <p className="dv-size-body">
          Experience the dynamic circuit layout of our MX4 classes! Stations
          feature three exercises focusing on upper, lower, or full-body
          workouts. Dive into 45-minute sessions with 1-minute intervals, and
          create a diverse and challenging routine. On Saturdays, we offer an
          extended one-hour Weekend Warrior Workout for an extra fitness boost!
        </p>
      </div>
      <div className="dv-mx4__equipment">
        <p className="dv-eyebrow">Equipment</p>
        <ol aria-label={`MX4 equipment: ${equipmentSummary}`}>
          {equipment.map((item, index) => (
            <li className="dv-size-body" key={item}>
              <span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              {item}
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section className="dv-mx4__benefits" aria-labelledby="mx4-benefits-title">
      <div>
        <p className="dv-eyebrow">Four training outcomes</p>
        <h2 className="dv-size-heading" id="mx4-benefits-title">Benefits</h2>
      </div>
      <ol className="dv-mx4__benefit-list">
        {benefits.map((benefit, index) => (
          <li className="dv-size-body" key={benefit}>
            <span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
            <p className="dv-size-body">{benefit}</p>
          </li>
        ))}
      </ol>
    </section>

    <MX4CoachProfiles onOpenShowcase={openCoaches} />
    <MX4CoachShowcase open={coachesOpen} onOpenChange={setCoachesOpen} />

    <section className="dv-mx4__rentals" aria-labelledby="mx4-rentals-title">
      <div>
        <p className="dv-eyebrow">Private and group lessons</p>
        <h2 className="dv-size-heading"
          id="mx4-rentals-title"
          aria-label="MX4 Studio Rentals / Private and Group Lessons"
        >
          MX4 Studio Rentals
        </h2>
      </div>
      <div className="dv-mx4__rentals-copy">
        <p className="dv-size-body">
          Break free from extreme weather conditions, limited equipment, and
          space. Rent our state-of-the-art MX4 studio, equipped with the latest
          training equipment, for team practices, extra or targeted training
          days, and team-building events. Use an optimal training environment
          to help your athletes achieve maximum results. Feel the best—perform
          the best!
        </p>
        <Link className="dv-text-link" to={publicHref("/amenities/rentals")}>
          Explore MX4 Studio Rentals <span aria-hidden="true">↗</span>
        </Link>
      </div>
    </section>

    <section id="mx4-schedule" className="dv-mx4__schedule" aria-labelledby="mx4-schedule-title">
      <div className="dv-mx4__schedule-introduction">
        <p className="dv-eyebrow">Updated live</p>
        <h2 className="dv-size-heading" id="mx4-schedule-title">MX4 Class Schedule</h2>
        <p className="dv-size-body">
          This schedule opens on MX4 classes. Pick a day, or tap a class for
          details or to reserve a spot.
        </p>
      </div>
      <div className="dv-mx4__schedule-frame">
        <ClassSchedule
          focus="mx4"
          categoryId="5"
          title="Delta Valley Health Club MX4 weekly schedule"
        />
      </div>
    </section>

    <section className="dv-mx4__next-step" aria-labelledby="mx4-next-step-title">
      <div className="dv-mx4__next-step-heading">
        <p className="dv-eyebrow">Commit to Fit</p>
        <h2 className="dv-size-heading" id="mx4-next-step-title">
          <span>Choose your</span>
          <em>next move.</em>
        </h2>
      </div>
      <div className="dv-mx4__next-step-actions">
        <p className="dv-size-body">
          Join Brentwood&apos;s top fitness destination—your one-stop shop for all
          your fitness needs. Prioritize your health and join today!
        </p>
        <div>
          <a className="dv-button dv-button--light" href="#mx4-inquiry">Enroll Today</a>
          <Link className="dv-button dv-button--outline" to={publicHref("/book-a-tour")}>Book a Tour</Link>
          <Link className="dv-text-link" to={publicHref("/membership")}>
            Membership <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </div>
    </section>

    <ProgramJourney
      id="mx4-inquiry" tone="red" testId="mx4-inquiry-journey" photo="P02"
      source="mx4-interest" location="Brentwood" defaultInterests={['Small Group Training']}
      eyebrow="MX4 Membership Inquiry"
      title={['Start the conversation.', 'Your MX4 request, in about a minute.']}
      lede="Share your contact information and our Brentwood team will follow up with current MX4 information."
      note={<Link to={publicHref("/programs")}>Return to All Programs</Link>}
      planTitle="Your MX4 request" request={['Program', 'MX4 small-group training']}
      reviewNoun="MX4 request" startLabel="Start My MX4 Request" buttonText="Request MX4 Information"
      successTitle="MX4 Request Received"
      successMessage="Our Brentwood team will contact you with current MX4 information."
    />
  </main>
  );
};

export default MX4Page;
